import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/api';
import {
  Upload, Wand2, Loader2, ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle,
  Sparkles, Brain, Database, RotateCcw,
} from 'lucide-react';

// Canonical schema fields per sheet — must mirror backend SHEET_SCHEMAS
const SCHEMA = {
  Properties:  ['property_code', 'property_name', 'city', 'district', 'property_type', 'owner_name', 'total_units', 'notes'],
  Units:       ['unit_code', 'property_code', 'unit_number', 'unit_type', 'bedrooms', 'bathrooms', 'size_sqm', 'annual_rent', 'status', 'notes'],
  Tenants:     ['tenant_code', 'tenant_name', 'phone', 'email', 'national_id', 'unit_code', 'tenant_status', 'notes'],
  Leases:      ['lease_code', 'tenant_code', 'unit_code', 'start_date', 'end_date', 'annual_rent', 'payment_frequency', 'payment_status', 'security_deposit', 'notes'],
  Payments:    ['payment_code', 'lease_code', 'tenant_code', 'amount', 'due_date', 'payment_type', 'status', 'payment_method', 'paid_date', 'paid_amount', 'late_fee', 'notes'],
  Maintenance: ['ticket_code', 'unit_code', 'tenant_code', 'title', 'category', 'priority', 'status', 'description', 'vendor_name', 'estimated_cost', 'actual_cost', 'assigned_at', 'completed_at', 'notes'],
  Documents:   ['document_code', 'name', 'document_type', 'related_type', 'related_code', 'file_url', 'expiry_date', 'notes'],
};
const ALL_SHEETS = Object.keys(SCHEMA);

const ConfidenceBadge = ({ value }) => {
  const palette = value >= 90 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : value >= 70 ? 'bg-amber-50 text-amber-700 border-amber-200'
                : value >  0  ? 'bg-orange-50 text-orange-700 border-orange-200'
                              : 'bg-gray-50 text-gray-500 border-gray-200';
  return <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-md border ${palette}`}>{value || 0}%</span>;
};

const EngineBadge = ({ engine }) => {
  const map = {
    exact: { label: 'EXACT', color: 'text-emerald-700' },
    fuzzy: { label: 'FUZZY', color: 'text-blue-700' },
    ai:    { label: 'AI',    color: 'text-purple-700' },
  };
  const cfg = map[engine];
  if (!cfg) return null;
  return <span className={`text-[9px] font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>;
};

export default function AIColumnMapper({ isAr, onBack, onComplete }) {
  const [stage, setStage] = useState('upload'); // upload · scanning · mapping · importing · success · failure
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);  // backend AI detection report
  const [uploadId, setUploadId] = useState(null);
  const [overrides, setOverrides] = useState({}); // {sheetSourceName: {sourceHeader: targetField}}
  const [sheetTargets, setSheetTargets] = useState({}); // user-confirmed sheet match (overrides AI)
  const [importResult, setImportResult] = useState(null);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      setError(isAr ? 'يرجى اختيار ملف .xlsx' : 'Please choose a .xlsx file'); return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError(isAr ? 'الملف كبير (10MB كحد أقصى)' : 'File too large (10MB max)'); return;
    }
    setFile(f); setError('');
  };

  const loadSampleMessyFile = async () => {
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-import/sample-messy-workbook`);
      if (!res.ok) throw new Error('Failed to load sample');
      const blob = await res.blob();
      const sampleFile = new File([blob], 'REVA_Sample_Customer_File.xlsx', { type: blob.type });
      setFile(sampleFile);
    } catch (e) {
      setError(isAr ? 'تعذّر تحميل الملف التجريبي' : 'Could not load sample file');
    }
  };

  const runDetection = async () => {
    if (!file) return;
    setStage('scanning'); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.aiDetectSchema(fd);
      setReport(r.data.report);
      setUploadId(r.data.upload_id);
      // Seed overrides with AI suggestions
      const seed = {}; const sheets = {};
      for (const sh of r.data.report.sheets) {
        seed[sh.source_name] = {};
        for (const h of sh.headers) {
          seed[sh.source_name][h.source] = h.target || '';
        }
        sheets[sh.source_name] = sh.matched_sheet || '';
      }
      setOverrides(seed); setSheetTargets(sheets);
      setStage('mapping');
    } catch (e) {
      setError(e?.response?.data?.detail || (isAr ? 'فشل التحليل' : 'Detection failed'));
      setStage('upload');
    }
  };

  // Per-sheet stats: how many headers mapped (after user overrides)
  const sheetStats = (sourceSheetName) => {
    const headers = overrides[sourceSheetName] || {};
    const targetSheet = sheetTargets[sourceSheetName];
    if (!targetSheet) return { mapped: 0, total: Object.keys(headers).length, missing: [] };
    const required = (SCHEMA[targetSheet] || []).filter(f => ['property_code','property_name','city','property_type','total_units',
                                                              'unit_code','unit_number','unit_type','annual_rent',
                                                              'tenant_code','tenant_name','phone',
                                                              'lease_code','tenant_code','start_date','end_date',
                                                              'payment_code','lease_code','amount','due_date',
                                                              'ticket_code','unit_code','title','category',
                                                              'document_code','name','document_type'].includes(f));
    const used = new Set(Object.values(headers).filter(Boolean));
    const missing = required.filter(r => !used.has(r));
    const mapped = Object.values(headers).filter(Boolean).length;
    return { mapped, total: Object.keys(headers).length, missing };
  };

  const overallReady = () => {
    // At minimum each of the 4 required sheets must have a target + no missing required field
    const requiredCanonical = ['Properties', 'Units', 'Tenants', 'Leases'];
    const reverseMap = {};
    for (const [src, tgt] of Object.entries(sheetTargets)) {
      if (tgt) reverseMap[tgt] = src;
    }
    for (const r of requiredCanonical) {
      if (!reverseMap[r]) return { ready: false, reason: isAr ? `لم يتم اختيار صفحة لـ${r}` : `No sheet selected for ${r}` };
      const stats = sheetStats(reverseMap[r]);
      if (stats.missing.length) return { ready: false, reason: isAr ? `حقول مفقودة في ${r}: ${stats.missing.join(', ')}` : `Missing fields in ${r}: ${stats.missing.join(', ')}` };
    }
    return { ready: true };
  };

  const submitImport = async () => {
    setStage('importing'); setError('');
    try {
      const mappings = {};
      for (const [src, target] of Object.entries(sheetTargets)) {
        if (!target) continue;
        mappings[src] = { matched_sheet: target, headers: overrides[src] || {} };
      }
      const r = await api.confirmMapping(uploadId, mappings);
      if (r.data.imported) {
        setImportResult(r.data);
        setStage('success');
        if (onComplete) onComplete();
      } else {
        setError(JSON.stringify(r.data.validation));
        setStage('failure');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || (isAr ? 'فشل الاستيراد' : 'Import failed'));
      setStage('failure');
    }
  };

  // ─────────── UPLOAD STAGE ───────────
  if (stage === 'upload') {
    return (
      <Card className="shadow-soft border-gray-100">
        <CardContent className="pt-6 pb-6 space-y-5">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-xs text-gray-500 hover:text-primary flex items-center gap-1" data-testid="ai-mapper-back">
              <ArrowLeft className="h-3.5 w-3.5" /> {isAr ? 'رجوع' : 'Back'}
            </button>
            <span className="text-xs text-gray-300">·</span>
            <span className="text-xs text-gray-500">{isAr ? 'مطابقة الأعمدة بالذكاء الاصطناعي' : 'AI-assisted column mapping'}</span>
          </div>

          <div>
            <h2 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-primary" />
              {isAr ? 'ارفع ملفك بأي تنسيق' : 'Upload your file in any format'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isAr
                ? 'أعمدة عربية أو إنجليزية، أسماء صفحات مختلفة، ترتيب أعمدة عشوائي — REVA يفهم كل ذلك.'
                : 'Arabic or English columns, oddly-named sheets, random column order — REVA understands all of it.'}
            </p>
          </div>

          <label className="block border-2 border-dashed border-gray-200 rounded-2xl p-10 text-center hover:border-primary/40 transition-colors cursor-pointer" data-testid="ai-drop">
            <input type="file" className="hidden" accept=".xlsx" onChange={e => handleFile(e.target.files[0])} data-testid="ai-file-input" />
            <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            {file ? (
              <div>
                <p className="font-semibold text-primary">{file.name}</p>
                <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
            ) : (
              <div>
                <p className="font-medium text-gray-900">{isAr ? 'اسحب وأفلت أو اضغط للاختيار' : 'Drag & drop or click to choose'}</p>
                <p className="text-xs text-gray-500 mt-1">{isAr ? 'فقط .xlsx · 10MB كحد أقصى' : '.xlsx files only · 10MB max'}</p>
              </div>
            )}
          </label>

          {/* Try-with-sample row — for demos & first-time users */}
          <div className="flex items-center justify-center gap-2 text-xs">
            <span className="text-gray-400">{isAr ? 'لا يوجد ملف؟' : 'Don\'t have a file handy?'}</span>
            <button
              type="button"
              onClick={loadSampleMessyFile}
              className="font-semibold text-primary hover:text-primary/80 inline-flex items-center gap-1.5 underline-offset-2 hover:underline"
              data-testid="ai-load-sample-btn"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {isAr ? 'جرّب بملف تجريبي عشوائي' : 'Try with a sample messy file'}
            </button>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-xl flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />{error}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={runDetection} disabled={!file} className="gradient-primary text-white rounded-xl" data-testid="ai-detect-btn">
              <Brain className="h-4 w-4 me-2" />
              {isAr ? 'حلّل بالذكاء الاصطناعي' : 'Analyze with AI'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─────────── SCANNING / IMPORTING STAGE ───────────
  if (stage === 'scanning' || stage === 'importing') {
    const label = stage === 'scanning'
      ? (isAr ? 'جاري التحليل بالذكاء الاصطناعي...' : 'Analyzing with AI...')
      : (isAr ? 'جاري الاستيراد...' : 'Importing...');
    return (
      <Card className="shadow-soft border-gray-100">
        <CardContent className="py-16 flex flex-col items-center justify-center text-center gap-3">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
            <Loader2 className="h-7 w-7 text-primary animate-spin" />
          </div>
          <div className="font-heading font-bold text-gray-900">{label}</div>
          <div className="text-xs text-gray-500 max-w-sm">
            {stage === 'scanning'
              ? (isAr ? 'يفحص REVA كل صفحة وعمود ويطابقها مع البنية المعيارية. قد يستغرق ذلك 5-15 ثانية.' : 'REVA is scanning every sheet and column and matching them to the canonical schema. This takes 5-15 seconds.')
              : (isAr ? 'إعادة كتابة الأعمدة، التحقق، والإدخال في قاعدة البيانات.' : 'Rewriting columns, validating, and inserting into the database.')}
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─────────── MAPPING STAGE — the heart of the UI ───────────
  if (stage === 'mapping' && report) {
    const ready = overallReady();
    return (
      <div className="space-y-4">
        {/* Header strip */}
        <Card className="shadow-soft border-gray-100">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <h2 className="font-heading text-lg font-bold text-gray-900">{isAr ? 'تأكيد المطابقة' : 'Confirm column mapping'}</h2>
                </div>
                <p className="text-xs text-gray-500">{isAr ? 'راجع الاقتراحات وعدّلها إذا لزم — كل تغيير يُحفظ تلقائياً.' : 'Review the suggestions and override anything that\'s wrong — every change auto-saves.'}</p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="text-gray-700"><b>{report.summary.headers_matched}</b>/{report.summary.headers_total} {isAr ? 'عمود' : 'columns'}</span>
                </div>
                {report.summary.headers_via_ai > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Brain className="h-3.5 w-3.5 text-purple-600" />
                    <span className="text-gray-700"><b>{report.summary.headers_via_ai}</b> {isAr ? 'عبر AI' : 'via AI'}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Per-sheet mapping cards */}
        {report.sheets.map((sh, idx) => {
          const stats = sheetStats(sh.source_name);
          const tgt = sheetTargets[sh.source_name] || '';
          const headers = overrides[sh.source_name] || {};
          // available targets = canonical fields not yet used in this sheet
          const usedTargets = new Set(Object.values(headers).filter(Boolean));
          const sheetFields = SCHEMA[tgt] || [];
          return (
            <Card key={idx} className="shadow-soft border-gray-100">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <div className="px-2 py-0.5 bg-gray-900 text-white text-[10px] font-mono font-bold rounded">{sh.source_name}</div>
                    <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
                    <select
                      value={tgt}
                      onChange={e => setSheetTargets(prev => ({ ...prev, [sh.source_name]: e.target.value }))}
                      className="px-2 py-1 text-xs font-bold bg-primary/10 text-primary border border-primary/20 rounded hover:bg-primary/15 cursor-pointer"
                      data-testid={`sheet-target-${idx}`}
                    >
                      <option value="">{isAr ? '— تجاهل —' : '— Skip —'}</option>
                      {ALL_SHEETS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-gray-500">{stats.mapped}/{stats.total} {isAr ? 'مرتبط' : 'mapped'}</span>
                    {stats.missing.length > 0 && (
                      <span className="text-red-700 font-medium flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />{isAr ? 'ناقص:' : 'Missing:'} {stats.missing.join(', ')}
                      </span>
                    )}
                  </div>
                </div>

                {!tgt ? (
                  <div className="text-xs text-gray-400 py-3">{isAr ? 'هذه الصفحة سيتم تجاهلها أثناء الاستيراد.' : 'This sheet will be skipped during import.'}</div>
                ) : (
                  <div className="space-y-1.5">
                    {sh.headers.map((h, i) => {
                      const currentTarget = headers[h.source] || '';
                      // exclude targets already used by other rows in this sheet (but keep this row's own current pick available)
                      const opts = sheetFields.filter(f => !usedTargets.has(f) || f === currentTarget);
                      return (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-gray-50/60 hover:bg-white border border-transparent hover:border-gray-200 rounded-md transition-all">
                          <div className="col-span-4 truncate">
                            <div className="text-xs font-mono font-semibold text-gray-900 truncate">{h.source}</div>
                            {sh.sample?.[0]?.[i] && (
                              <div className="text-[10px] text-gray-400 truncate">e.g. {String(sh.sample[0][i]).substring(0, 30)}</div>
                            )}
                          </div>
                          <div className="col-span-1 text-center text-gray-300 text-xs">→</div>
                          <div className="col-span-5">
                            <select
                              value={currentTarget}
                              onChange={e => setOverrides(prev => ({
                                ...prev,
                                [sh.source_name]: { ...(prev[sh.source_name] || {}), [h.source]: e.target.value }
                              }))}
                              className="w-full px-2 py-1.5 text-xs font-mono bg-white border border-gray-200 rounded hover:border-primary/40 focus:border-primary focus:outline-none cursor-pointer"
                              data-testid={`col-map-${idx}-${i}`}
                            >
                              <option value="">{isAr ? '— تجاهل —' : '— Skip —'}</option>
                              {opts.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                          <div className="col-span-2 flex items-center justify-end gap-1.5">
                            <EngineBadge engine={h.engine} />
                            <ConfidenceBadge value={h.confidence} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {!ready.ready && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div><b>{isAr ? 'لا يمكن الاستيراد بعد:' : 'Not ready to import yet:'}</b> {ready.reason}</div>
          </div>
        )}

        <div className="flex justify-between gap-2">
          <Button onClick={() => { setStage('upload'); setReport(null); }} variant="outline" className="rounded-xl" data-testid="ai-mapper-restart">
            <RotateCcw className="h-4 w-4 me-2" /> {isAr ? 'ابدأ مع ملف آخر' : 'Start with another file'}
          </Button>
          <Button onClick={submitImport} disabled={!ready.ready} className="gradient-primary text-white rounded-xl" data-testid="ai-mapper-import-btn">
            <Database className="h-4 w-4 me-2" /> {isAr ? 'استيراد البيانات' : 'Import the data'}
          </Button>
        </div>
      </div>
    );
  }

  // ─────────── SUCCESS ───────────
  if (stage === 'success' && importResult) {
    return (
      <Card className="shadow-glass border-green-200 bg-gradient-to-br from-green-50/60 via-white to-primary/5">
        <CardContent className="py-10">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="h-9 w-9 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'استيراد ناجح بالذكاء الاصطناعي!' : 'AI Import Complete!'}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {isAr ? 'تم تحويل ملفك المخصص إلى بنية REVA المعيارية تلقائياً.' : 'Your custom file was auto-translated into REVA\'s canonical schema.'}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 max-w-4xl mx-auto">
            {Object.entries(importResult.inserted || {}).map(([k, v]) => (
              <div key={k} className="p-4 bg-white rounded-xl border border-gray-100 text-center" data-testid={`ai-success-${k}`}>
                <div className="text-3xl font-heading font-bold text-primary">{v}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">{k}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Button onClick={() => { setStage('upload'); setFile(null); setReport(null); setImportResult(null); }} variant="outline" className="rounded-xl">
              <RotateCcw className="h-4 w-4 me-2" /> {isAr ? 'استيراد ملف آخر' : 'Import another file'}
            </Button>
            <Button onClick={() => window.location.assign('/properties')} variant="outline" className="rounded-xl">
              {isAr ? 'عرض العقارات' : 'View properties'}
            </Button>
            <Button onClick={() => window.location.assign('/dashboard')} className="gradient-primary text-white rounded-xl" data-testid="ai-success-go-dashboard">
              {isAr ? 'الذهاب إلى لوحة التحكم' : 'Go to dashboard'} <ArrowRight className="h-4 w-4 ms-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─────────── FAILURE ───────────
  return (
    <Card className="shadow-soft border-red-100">
      <CardContent className="py-8 text-center">
        <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
        <h3 className="font-heading text-lg font-bold text-gray-900 mb-1">{isAr ? 'فشل الاستيراد' : 'Import failed'}</h3>
        <p className="text-xs text-gray-500 max-w-md mx-auto break-words">{error || (isAr ? 'حدث خطأ غير متوقع' : 'Unexpected error')}</p>
        <Button onClick={() => setStage('mapping')} variant="outline" className="rounded-xl mt-4">
          <ArrowLeft className="h-4 w-4 me-2" /> {isAr ? 'عُد إلى المطابقة' : 'Back to mapping'}
        </Button>
      </CardContent>
    </Card>
  );
}
